import { useEffect, useState } from 'react';
import axiosInstance from '../utils/axiosInstance';
import config from '../utils/config';
import { Note } from '../types';

// No cache. Notes UI is low-traffic and every consumer wants fresh data.
// Both hooks refetch on mount and whenever their id(s) change.

const fetchNotes = (deviceId: number): Promise<Note[]> =>
  axiosInstance
    .get<Note[]>(`${config.notesApiUrl}/notes/device/${deviceId}`)
    .then((res) => res.data)
    .catch((err) => {
      if (err?.response?.status === 403) return [];
      throw err;
    });

export function useDeviceNotes(deviceId: number | null | undefined): {
  notes: Note[];
  loading: boolean;
  error: string | null;
} {
  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (deviceId == null) {
      setNotes([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchNotes(deviceId)
      .then((data) => {
        if (cancelled) return;
        setNotes(data);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || 'Failed to fetch notes');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [deviceId]);

  return { notes, loading, error };
}

export function useDevicesNotes(deviceIds: number[]): Record<number, Note[]> {
  const [map, setMap] = useState<Record<number, Note[]>>({});
  // Stable key derived from sorted ids keeps the effect from refiring on every
  // parent render when the array identity changes but the contents don't.
  const key = [...deviceIds].sort((a, b) => a - b).join(',');

  useEffect(() => {
    if (deviceIds.length === 0) {
      setMap({});
      return;
    }
    let cancelled = false;
    Promise.all(
      deviceIds.map((id) =>
        fetchNotes(id)
          .then((notes) => [id, notes] as const)
          .catch(() => [id, [] as Note[]] as const),
      ),
    ).then((results) => {
      if (cancelled) return;
      const next: Record<number, Note[]> = {};
      results.forEach(([id, notes]) => {
        next[id] = notes;
      });
      setMap(next);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return map;
}
